import { defineStore } from 'pinia'

import dayjs from 'dayjs'
import { possessive } from 'i18n-possessive'
import { orderBy } from 'lodash-es'

import { featureFlags } from '@/lib/featureFlags'
import { getTeamYearText } from '@/lib/personalization'
import {
  buildSeniorPreorderPreview,
  resolveSeniorPreorderRoute,
  SeniorPreorderContext,
  type SeniorPreorderPreview,
  type SeniorPreorderResolution
} from '@/lib/seniorPreorder'
import { resolveSessionPhase, type SessionPhase } from '@/lib/sessionPhase'
import { rememberTermsAcceptance } from '@/lib/termsAcceptanceCookie'
import { sortMediaItemsByMediaTags } from '@/lib/utils'
import { parseYearbookContext } from '@/lib/yearbookContext'
import { PRIMARY_MARKET_LANDING_ROUTE, routeName } from '@/router/routeName'
import { appErrorService } from '@/services/AppErrorService'

import { CadyMarket } from '@/types/CadyMarket'
import { type CommerceStep, PostCatalogStepId, PreCatalogStepId } from '@/types/CommerceStep'
import { MediaLiteCanonical } from '@/types/MediaLiteCanonical'
import type { SegmentationKey } from '@/types/SegmentationKey'
import type { YearbookContext } from '@/types/YearbookContext'

import type {
  MediaLiteBase,
  ProofTypeId,
  SeniorPortfolioDetail,
  SessionTypes,
  SubjectProfileReturn,
  YearbookPoseResult
} from '@VelocitySquared/cady-sunny16-commerce-integration-models/core-api'

import { ContentfulContentModule } from '#contentful/ContentfulContentModule'

import { commerceStepsContent } from '@/content/commerceSteps'

export interface PersonalizationMap {
  SubjectFirstNamePossessive: string | null
  SubjectFirstName: string | null
  SubjectFirstNameAllCaps: string | null
  SubjectLastName: string | null
  SubjectSchoolId: number | null
  SubjectSchoolName: string | null
  SubjectSchoolYear: number | null
  SubjectPartialSchoolNames: string[] | null
  SubjectJerseyColors: string[] | null
  SubjectSportName: string | null
  SubjectTeamYear: string | null
  SubjectMascot: string | null
}

type SubjectSession = NonNullable<SubjectProfileReturn['Sessions']>[number]

interface MainStoreState {
  cadyMarket: CadyMarket | null
  cadySessionToken: string | null
  // Whether the user has accepted Terms & Conditions for the current session.
  // Persisted so the route guard and gate survive refresh; reset to false on
  // every genuine session entry (a fresh `?s=` token) so each new session must
  // re-accept. See `composables/dataLoader.ts` and the router T&C guard.
  termsAccepted: boolean
  dataExpiresAt: Date | null
  // Whether the customer's latest landing was the Senior Preorder page. Set by
  // `useDataLoader` on every landing load; see `isSeniorPreorderFlowActive`.
  enteredViaSeniorPreorder: boolean
  galleryImageLikes: number[]
  gtmCustomDimensions: Record<string, string> | null
  printSheetInfoModalContent: ContentfulContentModule | null
  portfolioTrackerAutoOpenedThisSession: boolean
  // `cartBundleId`s whose configuration the customer confirmed with Continue on
  // cart/configure (SUN-344). `cart.applyMaxPriceSelectionUpgrade()` skips these
  // and upgrades everything else the page renders on every load until then.
  // Persisted; cleared by `resetStore()`. Client-local on purpose: a bundle
  // downgraded back to $0 is indistinguishable from one never upgraded, so cart
  // state cannot answer "has the customer settled this".
  configurationConfirmedCartBundleIds: string[]
  showCartSidebar: boolean
  showModalAchievementVideo: boolean
  showPortfolioTrackerModal: boolean
  subject: SubjectProfileReturn | null
}

export const useMainStore = defineStore('main', {
  state: (): MainStoreState => ({
    // All state properties should be added to the manual resetStore action method below
    cadyMarket: null,
    cadySessionToken: null,
    termsAccepted: false,
    dataExpiresAt: null,
    enteredViaSeniorPreorder: false,
    galleryImageLikes: [],
    gtmCustomDimensions: null,
    portfolioTrackerAutoOpenedThisSession: false,
    configurationConfirmedCartBundleIds: [],
    printSheetInfoModalContent: null,
    showCartSidebar: false,
    showModalAchievementVideo: false,
    showPortfolioTrackerModal: false,
    subject: null
  }),
  getters: {
    commerceSteps(state: MainStoreState): {
      preCatalog: CommerceStep[]
      postCatalog: CommerceStep[]
    } {
      return {
        preCatalog: [
          {
            id: PreCatalogStepId.GALLERY,
            longName: commerceStepsContent.galleryStepName,
            shortName: commerceStepsContent.galleryStepName,
            catalogFlowStepId: null,
            routeUrl: this.marketLandingViewUrl,
            number: null,
            displaysBundleIds: null
          },
          {
            id: PreCatalogStepId.YEARBOOK,
            longName: commerceStepsContent.yearbookStepLongName,
            shortName: commerceStepsContent.yearbookStepShortName,
            catalogFlowStepId: null,
            routeUrl: this.router.resolve({ name: routeName.YEARBOOK }).href,
            number: null,
            displaysBundleIds: null
          }
        ],
        postCatalog: [
          {
            id: PostCatalogStepId.SELECT_POSES,
            longName:
              state.cadyMarket !== CadyMarket.Underclass
                ? commerceStepsContent.selectPosesStepLongName
                : commerceStepsContent.customizeOrderStepLongName,
            shortName:
              state.cadyMarket !== CadyMarket.Underclass
                ? commerceStepsContent.selectPosesStepShortName
                : commerceStepsContent.customizeOrderStepShortName,
            catalogFlowStepId: null,
            routeUrl: this.router.resolve({ name: routeName.CART_CONFIGURE }).href,
            number: null,
            displaysBundleIds: null
          },
          {
            id: PostCatalogStepId.RETOUCHING,
            longName: commerceStepsContent.retouchingStepName,
            shortName: commerceStepsContent.retouchingStepName,
            catalogFlowStepId: null,
            routeUrl: this.router.resolve({ name: routeName.RETOUCHING }).href,
            number: null,
            displaysBundleIds: null
          },
          {
            id: PostCatalogStepId.CHECKOUT,
            longName: commerceStepsContent.checkoutStepLongName,
            shortName: commerceStepsContent.checkoutStepShortName,
            catalogFlowStepId: null,
            routeUrl: this.router.resolve({ name: routeName.CART_CHECKOUT }).href,
            number: null,
            displaysBundleIds: null
          }
        ]
      }
    },
    marketLandingViewUrl(state: MainStoreState): string {
      if (state.cadyMarket) {
        try {
          return this.router.resolve({ name: PRIMARY_MARKET_LANDING_ROUTE[state.cadyMarket] }).href
        } catch {
          throw new Error('Could not resolve market url')
        }
      }

      return this.router.resolve({ name: routeName.HOME }).href
    },
    subjectMediaItemsCanonical(state: MainStoreState): MediaLiteCanonical[] {
      const mediaItemsCanonical: MediaLiteCanonical[] =
        state.subject?.MediaLites?.map((ml: MediaLiteBase) => {
          return {
            ...ml,
            CanonicalUrl: `cady-media://${ml.MediaId}`
          }
        }) ?? []

      return orderBy(mediaItemsCanonical, 'MediaId')
    },
    subjectMediaItemsCanonicalByCanonicalUrl(): (canonicalUrls: string[]) => MediaLiteCanonical[] {
      return (canonicalUrls: string[]) => {
        return this.subjectMediaItemsCanonical.filter((mi: MediaLiteCanonical) =>
          canonicalUrls.includes(mi.CanonicalUrl)
        )
      }
    },
    galleryImages(): MediaLiteCanonical[] {
      const mediaItemsFiltered = this.subjectMediaItemsCanonical.filter((mi: MediaLiteCanonical) =>
        mi.MediaTags?.includes('gallery')
      )

      return sortMediaItemsByMediaTags(mediaItemsFiltered)
    },
    capAndGownSceneImages(): MediaLiteCanonical[] {
      // Cap & Gown related scenes
      const capAndGownSceneIds = [
        'scene_52', // CapGown scene
        'scene_87' // EarlyCapGown scene
      ]

      return this.subjectMediaItemsCanonical.filter((mi: MediaLiteCanonical) =>
        mi.MediaTags?.some((mt) => capAndGownSceneIds.includes(mt))
      )
    },
    selectableImagesByAllowedMediaTags(): (allowedMediaTags: string[]) => MediaLiteCanonical[] {
      // Media tags that should never appear in selectable images, regardless of allowed tags.
      const selectionNeverAllowedMediaTags = [
        // HACK: Filter out media that have the 'virtual-pano' tag because it's meant to be a view-only in the gallery
        'virtual-pano'
      ]

      return (allowedMediaTags: string[]) => {
        if (allowedMediaTags.length === 0) {
          // if no allowed tags are provided, return all "default" tagged media items
          allowedMediaTags.push('default')
        }

        const mediaItemsFiltered = this.subjectMediaItemsCanonical.filter(
          (mi: MediaLiteCanonical) =>
            mi.MediaTags?.some((mt) => allowedMediaTags.includes(mt)) &&
            !mi.MediaTags?.some((mt) => selectionNeverAllowedMediaTags.includes(mt))
        )

        return sortMediaItemsByMediaTags(mediaItemsFiltered)
      }
    },
    isLikedGalleryImage(state: MainStoreState): (mediaId: number) => boolean {
      return (mediaId: number) => state.galleryImageLikes.includes(mediaId)
    },
    personalizationMap(state: MainStoreState): PersonalizationMap {
      const subjectFirstName = state.subject?.FirstName?.trim() ?? null

      return {
        SubjectFirstNamePossessive: subjectFirstName ? possessive(subjectFirstName, 'en') : null,
        SubjectFirstName: subjectFirstName,
        SubjectFirstNameAllCaps: subjectFirstName ? subjectFirstName?.toUpperCase() : null,
        SubjectLastName: state.subject?.LastName?.trim() ?? null,
        SubjectSchoolId: state.subject?.SchoolId ?? null,
        SubjectSchoolName: state.subject?.School?.SchoolName ?? null,
        SubjectSchoolYear: state.subject?.SchoolYear ?? null,
        SubjectPartialSchoolNames: state.subject?.TeamInfo?.SchoolNameParts ?? null,
        SubjectJerseyColors: state.subject?.TeamInfo?.JerseyColors ?? null,
        SubjectSportName: state.subject?.TeamInfo?.TeamSportName?.toUpperCase() ?? null,
        SubjectTeamYear: getTeamYearText(state.subject?.TeamInfo?.TeamYear) ?? null,
        SubjectMascot: state.subject?.TeamInfo?.Mascot?.toUpperCase() ?? null
      }
    },
    capAndGownPreviewImageUrl(): string | null {
      if (this.capAndGownSceneImages) {
        return this.capAndGownSceneImages[0]?.ThumbPath
      } else {
        return null
      }
    },
    graduationPreviewImageUrl(): string | null {
      // try to get the first grad stage scene media item
      const firstGradStageImage = this.subjectMediaItemsCanonical.find(
        (ml) => ml.MediaTags?.includes('scene_64') // graduation stage scene
      )

      if (firstGradStageImage) {
        return firstGradStageImage.ThumbPath
      }

      // try to get the first grad pose scene media item
      const firstGradPoseImage = this.subjectMediaItemsCanonical.find(
        (ml) => ml.MediaTags?.includes('scene_65') // graduation pose scene
      )

      if (firstGradPoseImage) {
        return firstGradPoseImage.ThumbPath
      }

      // can't find a graduation preview image
      return null
    },
    firstDefaultMediaItem(state: MainStoreState): MediaLiteBase | null {
      if (
        state.cadyMarket === CadyMarket.Underclass &&
        state.subject?.MediaLites &&
        state.subject.MediaLites.length > 0
      ) {
        return state.subject.MediaLites.find((ml) => ml.MediaTags?.includes('default')) ?? null
      }
      return null
    },
    // Underclass subjects can have an active session before their photo is
    // captured/processed. With nothing to review, the T&C gate is exempted
    // for them until a real photo exists; every other market is unaffected.
    //
    // Reads true while the subject is still unloaded — `firstDefaultMediaItem`
    // needs `subject` to find a photo, and `resetStores()` nulls it on session
    // entry. Consumers that render the gate UI must require a loaded subject
    // too, or the sheet shows during the load and blinks away once the profile
    // lands (see `showTermsGate` in `MarketLandingView`).
    termsAcceptanceGateExempt(): boolean {
      return this.cadyMarket === CadyMarket.Underclass && !this.firstDefaultMediaItem
    },
    // The subject's session for the active token, or null without a token or a match.
    tokenSession(state: MainStoreState): SubjectSession | null {
      if (!state.cadySessionToken) {
        return null
      }

      return (
        state.subject?.Sessions?.find((session) => session.Token === state.cadySessionToken) ?? null
      )
    },
    relatedObfuscatedSessionId(): string | null {
      return this.tokenSession?.ObfuscatedSessionId ?? null
    },
    gtmCustomDimensionsMerged(state: MainStoreState): Record<string, string> {
      let result: Record<string, string> = {}

      if (state.cadyMarket) {
        result['market'] = state.cadyMarket?.toString()
      }

      const schoolId = state.subject?.School?.SchoolId ?? state.subject?.SchoolId ?? null
      if (schoolId) {
        result['schoolId'] = schoolId.toString()
      }

      if (state.gtmCustomDimensions) {
        result = { ...result, ...state.gtmCustomDimensions }
      }

      return result
    },
    subjectMediaTags(): string[] {
      const mediaTags: string[] = []

      this.subjectMediaItemsCanonical.forEach((mediaItem) => {
        if (mediaItem.MediaTags) {
          mediaItem.MediaTags.forEach((tag) => {
            if (!mediaTags.includes(tag)) {
              mediaTags.push(tag)
            }
          })
        }
      })

      return orderBy(mediaTags)
    },
    subjectSessionEventDate(state: MainStoreState): Date | null {
      if (
        state.cadyMarket === CadyMarket.Underclass &&
        state.subject?.UnderclassPrepayCutoffDateUtc
      ) {
        return dayjs(state.subject?.UnderclassPrepayCutoffDateUtc).toDate()
      }

      if (
        state.cadyMarket !== CadyMarket.Underclass &&
        this.tokenSession?.StandardizedAppointmentDateUtc
      ) {
        return this.tokenSession.StandardizedAppointmentDateUtc
      }

      return null
    },
    subjectSessionEventDateAsIsoString(): string | null {
      if (this.subjectSessionEventDate) {
        return new Date(this.subjectSessionEventDate).toISOString()
      }

      return null
    },
    // `SessionStatusId` of the session matching the active token. Consumed by the
    // Senior Preorder resolver to route a past-date no-show (status in
    // `NON_ATTENDED_SESSION_STATUS_IDS`) to the dashboard rather than post-session
    // ordering (the handoff's "if not complete → Dashboard" rule). `null` when no
    // session matches. (`AttendedSession` proved unreliable — false even for
    // completed/QC sessions — so status id is the signal. See `lib/seniorPreorder.ts`.)
    subjectSessionStatusId(): number | null {
      return this.tokenSession?.SessionStatusId ?? null
    },
    // Market-agnostic temporal phase of the subject's session (pre / post /
    // did-not-occur), or null when the session date is unknown. The shared
    // primitive (ADR 2026.07.27-001) that feature resolvers consume — today the
    // Senior Preorder resolver. The session-date source itself varies by market
    // (see `subjectSessionEventDate`: Underclass cutoff vs the session appointment).
    subjectSessionPhase(): SessionPhase | null {
      return resolveSessionPhase({
        sessionDateUtc: this.subjectSessionEventDate,
        sessionStatusId: this.subjectSessionStatusId,
        now: new Date()
      })
    },
    subjectAvatarMediaItem(state: MainStoreState): MediaLiteCanonical | null {
      if (state.cadyMarket === CadyMarket.CapGown && this.capAndGownSceneImages.length > 0) {
        return this.capAndGownSceneImages[0]
      }

      if (state.cadyMarket === CadyMarket.Senior) {
        const seniorAvatarSceneIds = ['scene_25'] // Yearbook scene used as the Senior avatar

        const seniorSceneImage = this.subjectMediaItemsCanonical.find((ml) =>
          ml.MediaTags?.some((mt) => seniorAvatarSceneIds.includes(mt))
        )

        if (seniorSceneImage) {
          return seniorSceneImage
        }
      }

      if (state.cadyMarket === CadyMarket.Graduation) {
        const gradAvatarSceneIds = ['scene_65'] // scenes that can be used as graduation avatars

        const graduationSceneImage = this.subjectMediaItemsCanonical.find(
          (ml) => ml.MediaTags?.some((mt) => gradAvatarSceneIds.includes(mt)) // individual graduation scene
        )

        if (graduationSceneImage) {
          return graduationSceneImage
        }

        if (this.capAndGownSceneImages.length > 0) {
          return this.capAndGownSceneImages[0] // fallback to cap and gown scene
        }
      }

      if (state.cadyMarket === CadyMarket.Athletic) {
        const athleticAvatarSceneIds = ['scene_75'] // scenes that can be used as athletic avatars

        const athleticSceneImage = this.subjectMediaItemsCanonical.find(
          (ml) => ml.MediaTags?.some((mt) => athleticAvatarSceneIds.includes(mt)) // individual athletic scene
        )

        if (athleticSceneImage) {
          return athleticSceneImage
        }

        if (this.capAndGownSceneImages.length > 0) {
          return this.capAndGownSceneImages[0] // fallback to cap and gown scene
        }
      }

      if (state.cadyMarket === CadyMarket.Underclass && this.galleryImages.length > 0) {
        return this.galleryImages[0]
      }

      return null
    },
    subjectFullName(state: MainStoreState): string {
      if (state.subject?.FirstName && state.subject?.LastName) {
        return `${state.subject.FirstName} ${state.subject.LastName}`
      }

      if (state.subject?.FirstName) {
        return state.subject.FirstName
      }

      if (state.subject?.LastName) {
        return state.subject.LastName
      }

      return ''
    },
    subjectFirstGalleryMediaItem(): MediaLiteCanonical | null {
      if (
        this.subjectMediaItemsCanonical.length > 0 &&
        this.subjectMediaItemsCanonical[0].MediaTags?.includes('gallery')
      ) {
        return this.subjectMediaItemsCanonical.filter((smic) =>
          smic.MediaTags?.includes('gallery')
        )[0]
      }
      return null
    },
    segmentationKey(state: MainStoreState): SegmentationKey {
      const senior = state.subject?.SeniorDetail ?? null

      return {
        market: state.cadyMarket,
        sessionType: senior !== null ? (senior.SessionTypeId as SessionTypes) : null,
        proofType: senior !== null ? (senior.ProofTypeId as ProofTypeId) : null,
        schoolId: state.subject?.School?.SchoolId ?? state.subject?.SchoolId ?? null
      }
    },
    // Senior Preorder marketing-entry context (active/ended/prepaid/none) +
    // fallback target, resolved from the subject profile alone — no catalog. Read
    // by the data loader (a `none` result redirects out before catalog work), by
    // `PreorderView`, and by `isSeniorPreorderFlowActive`. Scoped to the Senior market inside the
    // resolver (`SeniorDetail` is subject-level, so the market has to be an input
    // rather than an assumption); non-Senior markets always resolve to `none`.
    seniorPreorderResolution(): SeniorPreorderResolution {
      return resolveSeniorPreorderRoute({
        market: this.segmentationKey.market,
        sessionType: this.segmentationKey.sessionType,
        proofType: this.segmentationKey.proofType,
        sessionPhase: this.subjectSessionPhase
      })
    },
    // Whether the customer is in the Senior Preorder flow: an `active` subject who
    // entered through the preorder page. Gates the app-wide preorder adjustments
    // (`commerce.ts` steps, `CheckoutView`'s step counter), so an `active` subject
    // who enters on `/senior` gets normal senior ordering.
    isSeniorPreorderFlowActive(): boolean {
      return (
        this.enteredViaSeniorPreorder &&
        this.seniorPreorderResolution.context === SeniorPreorderContext.Active
      )
    },
    // Dev-only dry-run summary of what the Senior Preorder resolver would do
    // with the current token — the resolved context plus display fields (enum
    // labels, computed `senior-preorder/…` path, plain-English intended action) —
    // WITHOUT any redirect or catalog fetch. Rendered by `PreorderView` in
    // `?preview` mode (dev server only) and logged to console.
    seniorPreorderPreview(): SeniorPreorderPreview {
      return buildSeniorPreorderPreview({
        market: this.segmentationKey.market,
        sessionType: this.segmentationKey.sessionType,
        proofType: this.segmentationKey.proofType,
        sessionDateUtc: this.subjectSessionEventDate,
        sessionStatusId: this.subjectSessionStatusId,
        deliveredCatalogPath: this.subject?.CatalogPath ?? null,
        now: new Date()
      })
    },
    portfolioTrackerDetail(state: MainStoreState): SeniorPortfolioDetail | null {
      if (!featureFlags.portfolioTracker) return null
      return state.subject?.SeniorDetail?.Portfolio ?? null
    },
    yearbookContext(state: MainStoreState): YearbookContext | null {
      // Pure view-model derived from `state.subject` via the adapter in
      // `lib/yearbookContext.ts`. Errors surfaced by the adapter are
      // reported once per subject load in `setSubject` — NOT here — to
      // avoid the New Relic flood that comes from Pinia recomputing this
      // getter on every dependency change.
      if (!featureFlags.yearbookSelection) return null
      return parseYearbookContext(state.subject, state.cadySessionToken).context
    }
  },
  actions: {
    // Ingest action for `subject`. Centralizes the one-shot data-shape
    // reporting that the Deviation7 ADR (§Part 1) and the yearbook adapter
    // both depend on, so each report fires once per subject load instead of
    // once per reactive recompute of a Pinia getter. Callers
    // (`composables/dataLoader.ts`, `views/CustomerService/OrderView.vue`)
    // assign through this action instead of touching `this.subject` directly.
    //
    // Two reporting passes:
    //   1. Yearbook adapter — five distinct data-inconsistency variants
    //      surfaced via `parseYearbookContext` in `lib/yearbookContext.ts`.
    //   2. Senior segmentation precondition — Senior subjects must carry
    //      `SeniorDetail.SessionTypeId` and `SeniorDetail.ProofTypeId`. The
    //      ADR's fail-closed stance still holds at each gating decision
    //      (retouching gate, yearbook gate, prooftype filter — all return
    //      false / hide when fields are null); this is the one place that
    //      reports the underlying data error.
    //
    // `reportYearbookInconsistencies` lets a caller skip pass 1. The Senior
    // Preorder flow passes `false`: yearbook selection never runs there
    // (pre-session — the subject has yearbook poses but no images yet), so the
    // "empty AvailableMediaItems" state is expected, not a data error to report.
    setSubject(
      subject: SubjectProfileReturn | null,
      options: { reportYearbookInconsistencies?: boolean } = {}
    ): void {
      this.subject = subject

      const { reportYearbookInconsistencies = true } = options
      if (reportYearbookInconsistencies && featureFlags.yearbookSelection && subject !== null) {
        const { errors } = parseYearbookContext(subject, this.cadySessionToken)
        for (const { error, metadata } of errors) {
          appErrorService.reportError(error, metadata)
        }
      }

      if (this.cadyMarket === CadyMarket.Senior && subject !== null) {
        const senior = subject.SeniorDetail ?? null
        if (senior === null || senior.SessionTypeId == null || senior.ProofTypeId == null) {
          appErrorService.reportError(
            new Error('Senior subject missing required segmentation fields'),
            {
              cadyMarket: this.cadyMarket,
              cadySessionToken: this.cadySessionToken,
              hasSeniorDetail: senior !== null,
              sessionType: senior?.SessionTypeId ?? null,
              proofType: senior?.ProofTypeId ?? null
            }
          )
        }
      }
    },
    toggleCartSidebar() {
      this.showCartSidebar = !this.showCartSidebar
    },
    openPortfolioTrackerModal(): void {
      this.showPortfolioTrackerModal = true
    },
    closePortfolioTrackerModal(): void {
      this.showPortfolioTrackerModal = false
    },
    markPortfolioTrackerAutoOpened(): void {
      this.portfolioTrackerAutoOpenedThisSession = true
    },
    // Records that Continue persisted these bundles' configuration on
    // cart/configure (SUN-344); the selection upgrade leaves them alone from
    // here on. Not called by the upgrade itself — an unconfirmed upgrade is
    // offered again. Cleared by `resetStore()`.
    markConfigurationConfirmed(cartBundleIds: string[]): void {
      const confirmed = new Set(this.configurationConfirmedCartBundleIds)
      cartBundleIds.forEach((id) => confirmed.add(id))
      this.configurationConfirmedCartBundleIds = [...confirmed]
    },
    // Optimistic local-state update from `POST /submit-yearbook-poses`'s
    // response. Walks `YearbookPoseResult[]`, finds the matching pose in
    // `subject.SeniorDetail.Yearbook.Poses` by `PoseNumber`, and writes
    // each successful row's `MediaId` into `pose.Selection`. Avoids a
    // full subject refetch (expensive) while keeping the slot-level UI
    // aligned if the customer back-navigates to /yearbook before the
    // next natural refetch.
    //
    // Server/client parity caveats:
    // - `SelectedBy` — not returned by the endpoint; set to null. Not
    //   read by the `yearbookContext` adapter or rendered anywhere.
    // - `MediaPath` — not returned; set to null. The adapter resolves the
    //   selected thumb from the pose's `AvailableMediaItems` DTO by `MediaId`
    //   (which the just-picked id is a member of), only falling back to
    //   `MediaPath` on selection drift — so a null here is fine.
    // - `SelectedOn` — approximated by `sentAt` (the timestamp the
    //   storefront stamped on the request). Server truth lands on the
    //   next subject refetch.
    applyYearbookSelectionResults(results: YearbookPoseResult[], sentAt: string): void {
      const yearbook = this.subject?.SeniorDetail?.Yearbook
      if (yearbook == null) return
      const selectedOn = new Date(sentAt)
      for (const r of results) {
        if (!r.Success) continue
        const pose = yearbook.Poses?.find((p) => p.PoseNumber === r.PoseNumber)
        if (pose == null) continue
        pose.Selection = {
          MediaId: r.MediaId,
          MediaPath: null,
          SelectedBy: null,
          SelectedOn: selectedOn
        }
      }
    },
    toggleGalleryImageLike(mediaId: number) {
      const index = this.galleryImageLikes.indexOf(mediaId)

      if (index === -1) {
        this.galleryImageLikes.push(mediaId)
      } else {
        this.galleryImageLikes.splice(index, 1)
      }
    },
    // Marks Terms & Conditions accepted for the current session. The route
    // guard and the landing-page gate both derive from this flag. Also
    // remembers the token in the accepted-tokens cookie so a future entry via
    // this same token (a reused or reminder `?s=` link) skips the gate — see
    // `lib/termsAcceptanceCookie`.
    acceptTerms(): void {
      this.termsAccepted = true
      if (this.cadySessionToken) {
        rememberTermsAcceptance(this.cadySessionToken)
      }
    },
    resetStore(): void {
      // This method is used to reset the store to its initial state manually because of the use of persist
      // Any state property added above should be reset here
      this.cadyMarket = null
      this.cadySessionToken = null
      this.termsAccepted = false
      this.enteredViaSeniorPreorder = false
      this.galleryImageLikes = []
      this.gtmCustomDimensions = null
      this.portfolioTrackerAutoOpenedThisSession = false
      this.configurationConfirmedCartBundleIds = []
      this.printSheetInfoModalContent = null
      this.showCartSidebar = false
      this.showModalAchievementVideo = false
      this.showPortfolioTrackerModal = false
      this.subject = null
      this.dataExpiresAt = null
    },
    // An action, not a getter: a getter would cache the clock reading
    isDataExpired(): boolean {
      if (!this.dataExpiresAt) {
        return true
      }

      return new Date() > new Date(this.dataExpiresAt)
    },
    setDataExpiration(): void {
      // by default data expires 5 minutes from now
      const expirationAfterNMinutes = 5
      const dayJsDataExpiresAtDefault = dayjs().add(expirationAfterNMinutes, 'minutes')
      let dayJsExpiresAtCustom: null | dayjs.Dayjs = null

      if (this.subjectSessionEventDate) {
        const dayJsNow = dayjs()
        const dayJsSubjectSessionEventDate = dayjs(this.subjectSessionEventDate)

        // but if subjectSessionEventDate is in the future and sooner than dayJsDataExpiresAtDefault, use that instead
        if (
          dayJsSubjectSessionEventDate.isAfter(dayJsNow) &&
          dayJsSubjectSessionEventDate.isBefore(dayJsDataExpiresAtDefault)
        ) {
          dayJsExpiresAtCustom = dayJsSubjectSessionEventDate
        }
      }

      this.dataExpiresAt = dayJsExpiresAtCustom
        ? dayJsExpiresAtCustom.toDate()
        : dayJsDataExpiresAtDefault.toDate()
    }
  },
  persist: true
})
